import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { gsap } from 'gsap';

const useMedia = (queries, values, defaultValue) => {
  const get = useCallback(() => values[queries.findIndex(q => matchMedia(q).matches)] ?? defaultValue, [queries, values, defaultValue]);

  const [value, setValue] = useState(get);

  useEffect(() => {
    const handler = () => setValue(get);
    queries.forEach(q => matchMedia(q).addEventListener('change', handler));
    return () => queries.forEach(q => matchMedia(q).removeEventListener('change', handler));
  }, [queries, get]);

  return value;
};

const useMeasure = () => {
  const ref = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useLayoutEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setSize({ width, height });
    });
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);

  return [ref, size];
};

//

const Masonry = ({
  items,
  ease = 'power3.out',
  duration = 0.6,
  stagger = 0.05,
  animateFrom = 'bottom',
  scaleOnHover = true,
  hoverScale = 0.95,
  blurToFocus = true,
  colorShiftOnHover = false
}) => {
  const columns = useMedia(
    ['(min-width:1500px)', '(min-width:1000px)', '(min-width:600px)', '(min-width:400px)'],
    [5, 4, 3, 2],
    1
  );
  const [containerRef, { width }] = useMeasure();
  const imagesReady = true;
  const [imageErrors, setImageErrors] = useState(new Set());
  const [loadedIds, setLoadedIds] = useState(new Set());

  const getInitialPosition = useCallback((item) => {
    const containerRect = containerRef.current?.getBoundingClientRect();
    if (!containerRect) return { x: item.x, y: item.y };

    let direction = animateFrom;
    if (animateFrom === 'random') {
      const dirs = ['top', 'bottom', 'left', 'right'];
      direction = dirs[Math.floor(Math.random() * dirs.length)];
    }

    switch (direction) {
      case 'top':
        return { x: item.x, y: -200 };
      case 'bottom':
        return { x: item.x, y: window.innerHeight + 200 };
      case 'left':
        return { x: -200, y: item.y };
      case 'right':
        return { x: window.innerWidth + 200, y: item.y };
      case 'center':
        return {
          x: containerRect.width / 2 - item.w / 2,
          y: containerRect.height / 2 - item.h / 2
        };
      default:
        return { x: item.x, y: item.y + 100 };
    }
  }, [animateFrom, containerRef]);

  // Removed eager preloading to allow native lazy-loading to optimize network usage

  const grid = useMemo(() => {
    if (!width) return [];
    const colHeights = new Array(columns).fill(0);
    const gap = 16;
    const totalGaps = (columns - 1) * gap;
    const columnWidth = (width - totalGaps) / columns;

    return items.map(child => {
      const col = colHeights.indexOf(Math.min(...colHeights));
      const x = col * (columnWidth + gap);
      const height = child.height;
      const y = colHeights[col];

      colHeights[col] += height + gap;
      return { ...child, x, y, w: columnWidth, h: height };
    });
  }, [columns, items, width]);

  // Compute container height so absolutely-positioned children don't collapse the section
  const containerHeight = useMemo(() => {
    if (!grid.length) return 0;
    return Math.max(...grid.map(g => g.y + g.h));
  }, [grid]);

  // Determine top-row items (y === 0) to give them higher fetch priority
  const topRowIds = useMemo(() => {
    const ids = new Set();
    for (const it of grid) {
      if (it.y === 0) ids.add(it.id);
    }
    return ids;
  }, [grid]);

  const hasMounted = useRef(false);

  useLayoutEffect(() => {
    if (!imagesReady) return;

    grid.forEach((item, index) => {
      const selector = `[data-key="${item.id}"]`;
      const animProps = { x: item.x, y: item.y, width: item.w, height: item.h };

      if (!hasMounted.current) {
        const start = getInitialPosition(item);
        gsap.fromTo(
          selector,
          {
            opacity: 0,
            x: start.x,
            y: start.y,
            width: item.w,
            height: item.h,
            ...(blurToFocus && { filter: 'blur(10px)' })
          },
          {
            opacity: 1,
            ...animProps,
            ...(blurToFocus && { filter: 'blur(0px)' }),
            duration: 0.8,
            ease: 'power3.out',
            delay: index * stagger
          }
        );
      } else {
        gsap.to(selector, {
          ...animProps,
          duration,
          ease,
          overwrite: 'auto'
        });
      }
    });

    hasMounted.current = true;
  }, [grid, imagesReady, stagger, animateFrom, blurToFocus, duration, ease, getInitialPosition]);

  // Responsive sizes hint for the browser to pick optimal image resolutions
  const sizesAttr = useMemo(() => (
    '(min-width:1500px) calc((100vw - 64px)/5), ' +
    '(min-width:1000px) calc((100vw - 48px)/4), ' +
    '(min-width:600px) calc((100vw - 32px)/3), ' +
    '(min-width:400px) calc((100vw - 16px)/2), ' +
    '100vw'
  ), []);

  const handleMouseEnter = (id, element) => {
    if (scaleOnHover) {
      gsap.to(`[data-key="${id}"]`, {
        scale: hoverScale,
        duration: 0.3,
        ease: 'power2.out'
      });
    }
    if (colorShiftOnHover) {
      const overlay = element.querySelector('.color-overlay');
      if (overlay) gsap.to(overlay, { opacity: 0.3, duration: 0.3 });
    }
  };

  const handleMouseLeave = (id, element) => {
    if (scaleOnHover) {
      gsap.to(`[data-key="${id}"]`, {
        scale: 1,
        duration: 0.3,
        ease: 'power2.out'
      });
    }
    if (colorShiftOnHover) {
      const overlay = element.querySelector('.color-overlay');
      if (overlay) gsap.to(overlay, { opacity: 0, duration: 0.3 });
    }
  };

  const handleImageError = (id) => {
    setImageErrors(prev => new Set([...(prev || new Set()), id]));
    console.error(`Failed to load image for item: ${id}`);
  };

  const handleImageLoad = (id) => {
    setLoadedIds(prev => new Set([...(prev || new Set()), id]));
  };

  return (
    <div ref={containerRef} className="relative w-full h-full" style={{ height: containerHeight }}>
      {grid.map(item => (
        <div
          key={item.id}
          data-key={item.id}
          className="absolute box-content cursor-pointer"
          style={{
            willChange: 'transform, width, height, opacity',
            width: item.w,
            height: item.h,
            transform: `translate(${item.x}px, ${item.y}px)`
          }}
          onClick={() => window.open(item.url, '_blank', 'noopener')}
          onMouseEnter={e => handleMouseEnter(item.id, e.currentTarget)}
          onMouseLeave={e => handleMouseLeave(item.id, e.currentTarget)}
        >
          <div className="relative w-full h-full rounded-[10px] shadow-[0px_10px_50px_-10px_rgba(0,0,0,0.2)] overflow-hidden">
            <img
              src={item.img}
              alt={`Gallery item ${item.id}`}
              className="w-full h-full object-cover"
              loading="lazy"
              decoding="async"
              fetchPriority={topRowIds.has(item.id) ? 'high' : 'low'}
              sizes={sizesAttr}
              width={Math.round(item.w)}
              height={Math.round(item.h)}
              referrerPolicy="no-referrer"
              onError={() => handleImageError(item.id)}
              onLoad={() => handleImageLoad(item.id)}
              style={{
                display: imageErrors.has(item.id) ? 'none' : 'block',
                opacity: loadedIds.has(item.id) ? 1 : 0,
                transition: 'opacity 250ms ease'
              }}
            />
            {!loadedIds.has(item.id) && !imageErrors.has(item.id) && (
              <div className="absolute inset-0 bg-gray-200 animate-pulse" />
            )}
            {imageErrors.has(item.id) && (
              <div className="w-full h-full flex items-center justify-center bg-gray-200 text-gray-500 text-sm">
                Image failed to load
              </div>
            )}
            {colorShiftOnHover && (
              <div className="color-overlay absolute inset-0 bg-gradient-to-tr from-pink-500/50 to-sky-500/50 opacity-0 pointer-events-none" />
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default Masonry;